import React from "react";

import cytoscape from "cytoscape";
import cola from "cytoscape-cola";
import css from "./Cytoscape.module.css";
import panzoom from "cytoscape-panzoom";
import PropTypes from "prop-types";
import Callbacks from "./Callbacks";
import { overlapRemoval } from "./overlapRemoval";
import { buildEdgeInfo, buildNodeInfo } from "./selectedElement";
import cytoscapeLasso from "cytoscape-lasso";
import fcose from "cytoscape-fcose";
import { cyStyle } from "./cyStyle";
import _ from "lodash";

cytoscape.use(cola);
cytoscape.use(fcose);
panzoom(cytoscape);
cytoscape.use(cytoscapeLasso);

class Cytoscape extends React.Component {
  constructor(props) {
    super(props);
    this.root = React.createRef();
    this.callbacks = null;
  }

  componentDidMount() {
    const layoutParams = { name: "preset" };
    const cy = drawNetwork(
      this.root.current,
      this.props.elements,
      layoutParams,
      this.props.selectElements,
      this.props.allowed_conversion,
      this.props.pathwayInternalId,
      this.props.network_type,
      this.props.protein_type,
      this.props.header_label
    );

    this.callbacks = new Callbacks(cy);
    this.props.setCallbacks(this.callbacks);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.elements !== prevProps.elements ||
      this.props.sessionId !== prevProps.sessionId
    ) {
      const layoutParams = { name: "preset" };
      const cy = drawNetwork(
        this.root.current,
        this.props.elements,
        layoutParams,
        this.props.selectElements,
        this.props.allowed_conversion,
        this.props.pathwayInternalId,
        this.props.network_type,
        this.props.protein_type,
        this.props.header_label
      );
      this.callbacks = new Callbacks(cy);
      this.props.setCallbacks(this.callbacks);
    }
  }

  render() {
    return (
      <div className={css.cytoscape}>
        <div ref={this.root} className={css.inner} />
      </div>
    );
  }
}

export default Cytoscape;

function drawNetwork(
  container,
  elements,
  layout,
  selectElements,
  allowed_conversion,
  pathwayInternalId,
  network_type,
  protein_type,
  header_label
) {
  return setupCy(
    {
      elements: elements,
      container: container,
      layout: layout,
      style: cyStyle,
    },
    selectElements,
    allowed_conversion,
    pathwayInternalId,
    network_type,
    protein_type,
    header_label
  );
}

export function setupCy(
  cytoSetup,
  selectElements,
  allowed_conversion,
  pathwayInternalId,
  network_type,
  protein_type,
  header_label
) {
  const cy = cytoscape(cytoSetup);
  overlapRemoval(cy);

  // TODO: decomment here for panzoom
  const defaults = {
    zoomFactor: 0.05, // zoom factor per zoom tick
    zoomDelay: 45, // how many ms between zoom ticks
    minZoom: 0.1, // min zoom level
    maxZoom: 10, // max zoom level
    fitPadding: 50, // padding when fitting
    panSpeed: 10, // how many ms in between pan ticks
    panDistance: 10, // max pan distance per tick
    panDragAreaSize: 75, // the length of the pan drag box in which the vector for panning is calculated (bigger = finer control of pan speed and direction)
    panMinPercentSpeed: 0.25, // the slowest speed we can pan by (as a percent of panSpeed)
    panInactiveArea: 8, // radius of inactive area in pan drag box
    panIndicatorMinOpacity: 0.5, // min opacity of pan indicator (the draggable nib); scales from this to 1.0
    zoomOnly: false, // a minimal version of the ui only with zooming (useful on systems with bad mousewheel resolution)
    fitSelector: undefined, // selector of elements to fit
    animateOnFit: function () {
      // whether to animate on fit
      return false;
    },
    fitAnimationDuration: 1000, // duration of animation on fit

    // icon class names
    sliderHandleIcon: "fa fa-minus",
    zoomInIcon: "fa fa-plus",
    zoomOutIcon: "fa fa-minus",
    resetIcon: "fa fa-expand",
  };

  // add the panzoom control
  cy.panzoom(defaults);

  cy.lassoSelectionEnabled(true);

  cy.minZoom(0.1);
  cy.maxZoom(20);

  const emptyTap = () => {
    selectElements([]);

    cy.nodes().forEach((node) => {
      node.removeClass("searched");
    });
  };

  cy.on("tap", emptyTap);

  // let labelsToReset = [];

  const onChangeLabel = (idNode, newLabel, newType, native) => {
    // labelsToReset.push({
    //   idNode: idNode,
    //   originalLabel: cy.$(`#${idNode}`).data().label,
    //   originalType: cy.$(`#${idNode}`).data().node_type,
    // });

    cy.$(`#${idNode}`).data("label", newLabel);
    cy.$(`#${idNode}`).data("node_type", newType);

    cy.$(`#${idNode}`).css({
      content: newLabel,
      color: native ? "black" : "darkred",
    });

    const dataNode = cy.$(`#${idNode}`).data();

    selectElements(
      buildNodeInfo(
        dataNode,
        allowed_conversion,
        pathwayInternalId,
        onChangeLabel,
        protein_type,
        header_label
      )
    );
  };

  // const resetAllLabels = () => {
  //   labelsToReset.map((entry) => {
  //     cy.$(`#${entry.idNode}`).data("label", entry.originalLabel);
  //     cy.$(`#${entry.idNode}`).data("node_type", entry.originalType);
  //
  //     cy.$(`#${entry.idNode}`).css({
  //       content: entry.originalLabel,
  //     });
  //   });
  // };

  cy.on("tap", "edge", function (event) {
    const dataEdge = event.target.data();
    const source = cy.$(`#${dataEdge.source}`).json().data;
    const target = cy.$(`#${dataEdge.target}`).json().data;
    selectElements(buildEdgeInfo(dataEdge, source, target, network_type));
  });

  cy.on("tap", "node", function (event) {
    const dataNode = event.target.data();
    selectElements(
      buildNodeInfo(
        dataNode,
        allowed_conversion,
        pathwayInternalId,
        onChangeLabel,
        protein_type,
        header_label
      )
    );
  });

  // FONTE: https://github.com/PathwayCommons/app-ui/blob/0b615e94829217336113de0fcedb22b0d6c142bf/src/client/features/paint/cy/events.js#L66
  let highlightNode = _.debounce((e) => {
    let selectedNode = e.target;
    let elesToHighlight = cy.collection();

    //Create a list of the hovered node & its neighbourhood
    selectedNode
      .neighborhood()
      .nodes()
      .union(selectedNode)
      .forEach((node) => {
        elesToHighlight.merge(node.ancestors());
        elesToHighlight.merge(node.descendants());
        elesToHighlight.merge(node);
      });
    elesToHighlight.merge(selectedNode.neighborhood().edges());

    //Add highlighted class to node & its neighbourhood, unhighlighted to everything else
    cy.elements().addClass("semitransp");
    elesToHighlight.forEach((ele) => {
      ele.removeClass("semitransp");
      ele.addClass("highlight");
    });
  }, 500);

  cy.on("mouseover", 'node[node_type!="compartment"]', highlightNode);
  cy.on("mouseout", 'node[node_type!="compartment"]', function (e) {
    highlightNode.cancel();
    cy.elements().removeClass("semitransp");
    cy.elements().removeClass("highlight");
  });

  return cy;
}

Cytoscape.propTypes = {
  selectElements: PropTypes.func,
  setCallbacks: PropTypes.func,
};
